import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { actions as warningActions } from 'state/warnings';
import { actions as errorActions } from 'state/errors';

import {
  actions as orderActions,
  selectors as orderSelectors,
} from 'state/order';
import {
  actions as shipmentsActions,
  selectors as shipmentsSelectors,
} from 'state/shipments';

import { formatters as addressFormatters } from 'utils/addresses';
import { isEmpty } from 'utils/types';
import keyGenerator from 'utils/keys';

import {
  constants as dateAndTimeConstants,
  formatters as dateAndTimeFormatters,
  validators as dateAndTimeValidators,
} from 'utils/dateAndTimes';

import types from 'state/direct-order/shipments/actionTypes';
import utils from 'state/direct-order/shipments/utils';
import { setTimeToIfNeeded } from 'state/shipments/effects';

function* correctTimes(shipmentKey) {
  const times = yield select(shipmentsSelectors.getShipmentTimes, shipmentKey);
  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    times,
    shipmentKey
  );
  if (!isEmpty(errors)) {
    const cleaned = Object.keys(errors).reduce(
      (acc, errorKey) => ({
        ...acc,
        [errorKey]: '',
      }),
      {}
    );

    const warnings = Object.keys(errors).reduce(
      (acc, errorKey) => ({
        ...acc,
        [keyGenerator.dateTime({
          shipmentKey,
          dateTimeKey: errorKey,
        })]: 'warning',
      }),

      {}
    );

    yield all([
      put(
        shipmentsActions.changeValues({ values: { ...cleaned }, shipmentKey })
      ),
      put(warningActions.setWarning(warnings)),
    ]);
  }
  return true;
}

function* handleChangeTime({ payload }) {
  const { stopType, timeParam, value, shipmentKey, diffDay } = payload;

  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);
  const input = yield call(dateAndTimeFormatters.formatRawTime, value);

  const originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  let computedTimes = { ...originalTimes, [key]: input };
  const datekey = yield call(utils.getDateKey, stopType, timeParam);
  const dateFromkey = yield call(utils.getDateFromKey, stopType);

  const newDateWithDiff = yield call(
    dateAndTimeFormatters.getDateFormatted,
    computedTimes[dateFromkey],
    diffDay
  );

  computedTimes = { ...computedTimes, [datekey]: newDateWithDiff };

  const autocompletedInput = yield call(
    dateAndTimeFormatters.autocompleteTimeInputIfCan,
    input
  );
  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    {
      ...computedTimes,
      [key]: autocompletedInput,
    },
    shipmentKey
  );

  const businessErrors = errors?.[key] || [];

  let isNextDay = false;

  let computedTimesIfInputIsForNextDay = { ...computedTimes };

  if (
    !isEmpty(businessErrors) &&
    diffDay === 0 &&
    timeParam !== dateAndTimeConstants.TIME_FIELD_PARAMS.from
  ) {
    const newDateWithNextDiff = yield call(
      dateAndTimeFormatters.getDateFormatted,
      computedTimes[dateFromkey],
      diffDay + 1
    );
    computedTimesIfInputIsForNextDay = {
      ...computedTimes,
      [datekey]: newDateWithNextDiff,
    };

    const { errors: nextDayErrors } = yield call(
      dateAndTimeValidators.asyncValidateTimes,
      computedTimesIfInputIsForNextDay,
      shipmentKey
    );

    if (!nextDayErrors[key]) isNextDay = true;
  }

  if (!isNextDay) {
    yield put(
      errorActions.setError({
        [fieldKey]: businessErrors,
      })
    );
  }

  const newValues = isNextDay
    ? { ...computedTimesIfInputIsForNextDay }
    : { ...computedTimes };

  yield put(
    shipmentsActions.changeValues({ values: { ...newValues }, shipmentKey })
  );
}

function* handleTimePickerBlur({ payload }) {
  const { stopType, timeParam, shipmentKey } = payload;
  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);
  let timeRaw = yield select(
    shipmentsSelectors.getShipmentTimeRaw,
    shipmentKey,
    null,
    key
  );

  yield put(errorActions.removeError([fieldKey]));

  if (isEmpty(timeRaw)) {
    yield put(orderActions.fetchQuote());
    return;
  }

  let originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  if (timeRaw.length >= 3) {
    const autocompletedInput = yield call(
      dateAndTimeFormatters.autocompleteTimeInputIfCan,
      timeRaw
    );
    timeRaw = autocompletedInput;
    originalTimes = { ...originalTimes, [key]: autocompletedInput };
  }

  const { isValid } = yield call(dateAndTimeValidators.validateTime, timeRaw);

  let businessErrors = false;

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    originalTimes,
    shipmentKey
  );
  if (!!errors && errors[key]) {
    businessErrors = errors[key];
  }

  const isEconomyModeEnabled = yield select(
    orderSelectors.getIsEconomyModeEnabled
  );

  if ((!isValid && timeRaw) || businessErrors) {
    if (
      !isEconomyModeEnabled &&
      !!businessErrors &&
      !businessErrors.some((r) =>
        ['MIN_dropoff_time_from_OTHER_DAY'].includes(r)
      )
    ) {
      yield put(warningActions.setWarning({ [fieldKey]: 'warning' }));
      yield put(
        shipmentsActions.changeValues({
          values: {
            [key]: '',
          },
          shipmentKey,
        })
      );
    }
  } else {
    const priorityRules = yield call(
      dateAndTimeValidators.priorityRules.time,
      key
    );

    const cleanedWithPriority = Object.keys(errors).reduce(
      (acc, errorKey) =>
        priorityRules?.indexOf(errorKey) >= 0 && !!originalTimes[errorKey]
          ? {
              ...acc,
              [errorKey]: '',
            }
          : { ...acc },
      {}
    );

    const warnings = Object.keys(errors).reduce(
      (acc, errorKey) =>
        priorityRules &&
        priorityRules.indexOf(errorKey) >= 0 &&
        !!originalTimes[errorKey]
          ? {
              ...acc,
              [keyGenerator.dateTime({
                shipmentKey,
                dateTimeKey: errorKey,
              })]: 'warning',
            }
          : { ...acc },

      {}
    );

    yield all([
      put(warningActions.setWarning(warnings)),
      put(
        shipmentsActions.changeValues({
          values: { ...cleanedWithPriority, [key]: timeRaw },
          shipmentKey,
        })
      ),
    ]);
    yield call(payload.setTimeToIfNeeded || setTimeToIfNeeded, {
      payload: { ...payload, time: timeRaw },
    });
  }
  yield put(orderActions.fetchQuote());
}

const shouldBeFixedWithPriorityRules = ({
  priorityRules,
  errorKey,
  computedTimes,
}) =>
  !!priorityRules &&
  priorityRules.indexOf(errorKey) >= 0 &&
  !!computedTimes[errorKey];

function* handleSelectTime({ payload }) {
  const { stopType, timeParam, value, shipmentKey, diffDay } = payload;

  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const input = yield call(dateAndTimeFormatters.formatRawTime, value);

  const originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  let computedTimes = { ...originalTimes, [key]: input };
  const datekey = yield call(utils.getDateKey, stopType, timeParam);
  const dateFromkey = yield call(utils.getDateFromKey, stopType);

  const newDateWithDiff = yield call(
    dateAndTimeFormatters.getDateFormatted,
    computedTimes[dateFromkey],
    diffDay
  );

  computedTimes = { ...computedTimes, [datekey]: newDateWithDiff };

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    computedTimes,
    shipmentKey
  );

  yield put(
    shipmentsActions.changeValues({ values: { ...computedTimes }, shipmentKey })
  );

  const priorityRules = yield call(
    dateAndTimeValidators.priorityRules.time,
    key
  );

  const computedValuesFixedWithPriority = Object.keys(errors).reduce(
    (acc, errorKey) =>
      shouldBeFixedWithPriorityRules({ priorityRules, errorKey, computedTimes })
        ? {
            ...acc,
            [errorKey]: '',
          }
        : { ...acc },
    {}
  );

  const warnings = Object.keys(errors).reduce(
    (acc, errorKey) =>
      shouldBeFixedWithPriorityRules({ priorityRules, errorKey, computedTimes })
        ? {
            ...acc,
            [keyGenerator.dateTime({
              shipmentKey,
              dateTimeKey: errorKey,
            })]: 'warning',
          }
        : { ...acc },

    {}
  );

  yield all([
    put(warningActions.setWarning(warnings)),
    put(
      shipmentsActions.changeValues({
        values: { ...computedValuesFixedWithPriority },
        shipmentKey,
      })
    ),
  ]);

  yield call(setTimeToIfNeeded, { payload: { ...payload, time: input } });
  yield put(orderActions.fetchQuote());
}

function* handleChangeDate({ payload }) {
  const { stopType, value, shipmentKey } = payload;
  const key = yield call(utils.getDateFromKey, stopType);

  const times = yield select(shipmentsSelectors.getShipmentTimes, shipmentKey);
  const diff = Math.floor(
    (new Date(value) - new Date(times[key])) / (1000 * 3600 * 24)
  );
  const dateKeys = yield call(utils.getDateKeys, stopType);

  const computedTimesWithDates = yield all(
    Object.keys(times).reduce(
      (acc, currKey) => ({
        ...acc,
        [currKey]:
          dateKeys.indexOf(currKey) >= 0
            ? call(dateAndTimeFormatters.getDateFormatted, times[currKey], diff)
            : times[currKey],
      }),
      {}
    )
  );

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    computedTimesWithDates,
    shipmentKey
  );

  const timeKeysByStop = yield call(utils.getTimeKeysByStop, stopType);
  const areErrorsFiltered = Object.keys(errors).reduce(
    (acc, keyError) => (timeKeysByStop.indexOf(keyError) >= 0 ? true : acc),
    false
  );

  const priorityRules = yield call(
    dateAndTimeValidators.priorityRules.date,
    stopType
  );

  const cleanedWithPriority = Object.keys(errors).reduce(
    (acc, errorKey) =>
      priorityRules && priorityRules.indexOf(errorKey) >= 0
        ? {
            ...acc,
            [keyGenerator.shipmentData.time({
              stopType: 'dropoff',
              timeParam: errorKey.split('_')[2],
            })]: '',
            [keyGenerator.shipmentData.date({
              stopType: 'dropoff',
              timeParam: errorKey.split('_')[2],
            })]: computedTimesWithDates.pickup_date_from,
          }
        : { ...acc },

    {}
  );

  if (areErrorsFiltered) {
    // Just clean and warn the time inputs that have errors
    const timeKeysToClean = timeKeysByStop.filter((tk) => !!errors[tk]);
    const timesCleaned = timeKeysToClean.reduce(
      (acc, curKey) => ({ ...acc, [curKey]: '' }),
      {}
    );

    const newWarnings = timeKeysToClean.reduce(
      (acc, curKey) => ({
        ...acc,
        [utils.getTimeKeyFull(shipmentKey, curKey)]: 'warning',
      }),
      {}
    );

    yield all([
      put(
        shipmentsActions.changeValues({
          values: {
            ...computedTimesWithDates,
            ...cleanedWithPriority,
            ...timesCleaned,
          },
          shipmentKey,
        })
      ),
      put(warningActions.setWarning(newWarnings)),
    ]);
  } else {
    yield put(
      shipmentsActions.changeValues({
        values: { ...computedTimesWithDates, ...cleanedWithPriority },
        shipmentKey,
      })
    );
  }
  yield put(orderActions.fetchQuote());
}

function* handleSubmitAddressForm({ payload }) {
  // In case of distribution, collection ... compute what shipments shouldChange
  const { stopType, address } = payload;

  const key = yield call(utils.getAddressKey, stopType);

  const rawKey = yield call(utils.getAddressRawKey, stopType);

  const rawAddress = yield call(addressFormatters.getAddressLabel, address);

  // call this action for all the shipments that needs to update the address
  yield put(
    shipmentsActions.changeAddress({
      ...payload,
      key,
      rawKey,
      rawAddress,
    })
  );
  yield put(orderActions.fetchQuote());
}

function* handleCleanAddress({ payload }) {
  // In case of distribution, collection ... compute what shipments shouldChange
  const { stopType } = payload;

  const key = yield call(utils.getAddressKey, stopType);

  const rawKey = yield call(utils.getAddressRawKey, stopType);

  // call this action for all the shipments that needs to update the address
  yield put(
    shipmentsActions.changeValues({
      values: {
        [rawKey]: '',
        [key]: null,
      },
      ...payload,
    })
  );
  yield put(orderActions.fetchQuote());
}

export default function* () {
  // timePickers
  yield takeLatest(types.changeTime$, handleChangeTime);
  yield takeLatest(types.selectTime$, handleSelectTime);
  yield takeLatest(types.onBlurTimePicker$, handleTimePickerBlur);

  // DatePickers
  yield takeLatest(types.changeDate$, handleChangeDate);

  // Addresses
  yield takeLatest(types.changeAddress$, handleSubmitAddressForm);
  yield takeLatest(types.cleanAddress$, handleCleanAddress);
}

export {
  correctTimes,
  handleChangeTime,
  handleTimePickerBlur,
  handleChangeDate,
  handleCleanAddress,
  handleSubmitAddressForm,
  handleSelectTime,
};
